import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Car,
  Bell,
  Heart,
  Users,
  MapPin,
  MessageSquare,
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Map as MapIcon,
  BarChart3
} from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  Typography,
  Divider,
  IconButton,
  Chip,
  Badge,
  Fade,
  Slide
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for enhanced visual effects
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 280,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
    position: 'relative',
    overflow: 'visible',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 50%, rgba(236, 72, 153, 0.1) 100%)',
      zIndex: -1,
    }
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  margin: '4px 8px',
  borderRadius: 12,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: active 
      ? 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)'
      : 'transparent',
    opacity: active ? 1 : 0,
    transition: 'opacity 0.3s ease',
    zIndex: -1,
  },
  '&:hover': {
    transform: 'translateX(4px)',
    '&::before': {
      opacity: 0.8,
    }
  },
  '& .MuiListItemIcon-root': {
    color: active ? '#ffffff' : '#64748b',
    transition: 'color 0.3s ease',
  },
  '& .MuiListItemText-primary': {
    color: active ? '#ffffff' : '#1e293b',
    fontWeight: active ? 600 : 500,
    transition: 'all 0.3s ease',
  }
}));

const GradientAvatar = styled(Avatar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
  border: '3px solid rgba(255, 255, 255, 0.2)',
}));

const AnimatedGradientBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: '4px',
  background: 'linear-gradient(180deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
  borderRadius: '0 4px 4px 0',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scaleY(1)',
    },
    '50%': {
      opacity: 0.7,
      transform: 'scaleY(1.1)',
    },
  },
}));

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const navItems = user ? [
    { name: 'Dashboard', path: '/dashboard', icon: Car },
    { name: 'Announcements', path: '/announcements', icon: Bell },
    { name: 'Connects', path: '/connects', icon: Heart },
    { name: 'Connect Requests', path: '/connect-requests', icon: Users },
    { name: 'Ride History', path: '/ride-history', icon: MapPin },
    { name: 'Map', path: '/map', icon: MapIcon },
    { name: 'Analytics', path: '/analytics', icon: BarChart3 },
    { name: 'Pending Feedback', path: '/pending-feedback', icon: MessageSquare },
  ] : [];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AnimatedGradientBar />
      
      {/* Header with Logo and User Info */}
      <Box sx={{ p: 3, textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Fade in timeout={800}>
          <Box>
            {user && user.profilePicture ? (
              <Avatar 
                src={user.profilePicture} 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  border: '4px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
                }} 
              />
            ) : (
              <GradientAvatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {user && user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Typography>
              </GradientAvatar>
            )}
            
            <Typography 
              variant="h4"
              component={Link}
              to="/profile"
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  opacity: 0.8
                }
              }}
            >
              LinkCab
            </Typography>
            
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Chip 
                  label="Premium User" 
                  size="small" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 2 }
                  }} 
                />
                <IconButton
                  onClick={() => setNotificationOpen(true)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      color: 'white',
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <Badge badgeContent={3} color="error">
                    <Bell size={20} />
                  </Badge>
                </IconButton>
              </Box>
            )}
          </Box>
        </Fade>
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, py: 2 }}>
        {user ? (
          <List>
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Slide direction="right" in timeout={300 + index * 100} key={item.path}>
                  <ListItem disablePadding>
                    <StyledListItemButton 
                      component={Link} 
                      to={item.path}
                      active={active ? 1 : 0}
                      onClick={() => setIsOpen(false)}
                      sx={{ 
                        '&:hover': {
                          '& .MuiListItemIcon-root': {
                            color: '#ffffff',
                          },
                          '& .MuiListItemText-primary': {
                            color: '#ffffff',
                          }
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Icon size={20} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.name} 
                        primaryTypographyProps={{
                          fontSize: '0.95rem',
                          fontWeight: active ? 600 : 500
                        }}
                      />
                    </StyledListItemButton>
                  </ListItem>
                </Slide>
              );
            })}
          </List>
        ) : (
          <Box sx={{ p: 2 }}>
            <Slide direction="right" in timeout={300}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StyledListItemButton 
                  component={Link} 
                  to="/login"
                  onClick={() => setIsOpen(false)}
                >
                  <ListItemText primary="Login" />
                </StyledListItemButton>
                <StyledListItemButton 
                  component={Link} 
                  to="/register"
                  onClick={() => setIsOpen(false)}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
                    color: 'white',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    }
                  }}
                >
                  <ListItemText primary="Get Started" />
                </StyledListItemButton>
              </Box>
            </Slide>
          </Box>
        )}
      </Box>

      {/* Footer Actions */}
      {user && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Slide direction="up" in timeout={600}>
            <Box>
              <StyledListItemButton 
                component={Link} 
                to="/profile"
                onClick={() => setIsOpen(false)}
              >
                <ListItemIcon>
                  <User size={20} />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </StyledListItemButton>
              
              <StyledListItemButton 
                component={Link} 
                to="/settings"
                onClick={() => setIsOpen(false)}
              >
                <ListItemIcon>
                  <Settings size={20} />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </StyledListItemButton>
              
              <StyledListItemButton 
                onClick={handleLogout}
                sx={{
                  color: '#ef4444',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <LogOut size={20} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </StyledListItemButton>
            </Box>
          </Slide>
        </Box>
      )}

      {/* Contact Info */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
          yugayatraretail.in
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem', display: 'block' }}>
          8320743025
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Hamburger */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'fixed', top: 16, left: 16, zIndex: 1200 }}>
        <IconButton
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 1)',
              transform: 'scale(1.05)',
            }
          }}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </IconButton>
      </Box>

      {/* Desktop Sidebar */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <StyledDrawer
          variant="permanent"
          open
          sx={{
            '& .MuiDrawer-paper': {
              position: 'fixed',
              height: '100vh',
            }
          }}
        >
          {drawerContent}
        </StyledDrawer>
      </Box>

      {/* Mobile Sidebar */}
      <StyledDrawer
        variant="temporary"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
          }
        }}
      >
        {drawerContent}
      </StyledDrawer>

      {/* Mobile Overlay */}
      {isOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
            zIndex: 1100,
            display: { xs: 'block', md: 'none' }
          }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Notification Center */}
      <NotificationCenter 
        open={notificationOpen} 
        onClose={() => setNotificationOpen(false)} 
      />
    </>
  );
};

export default Sidebar; 